import { useQuery } from '@apollo/client';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import { ReadOnlyDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { LoadingPane } from '~/components/loading-pane';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import AuditLogsIllustration from '~/images/illustrations/audit-logs.svg';
import {
  NetworkAuditLogRecordsDocument,
  type AuditLogRecordItem,
  type NetworkAuditLogRecords,
} from '../queries/records.generated';

interface RecordsProps {
  networkId: number;
  auditLogId: number;
}

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AuditLogsIllustration}
      description={t('pages.audit_logs.detail.no_records')}
    />
  );
};

export const Records = ({ networkId, auditLogId }: RecordsProps) => {
  const { t } = useTranslation();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading, error } = useQuery<NetworkAuditLogRecords>(
    NetworkAuditLogRecordsDocument,
    {
      variables: { networkId, auditLogId, page, perPage },
      onCompleted: (data) => handleOutOfBounds(data.network?.auditLog.records.pageInfo),
    },
  );

  const auditLog = data?.network?.auditLog;
  const records = auditLog?.records.nodes ?? [];
  const totalCount = useTotalCount(auditLog?.records.pageInfo.nodesCount);

  const columns = useMemo<GridColDef<AuditLogRecordItem>[]>(
    () => [
      {
        field: 'recordType',
        headerName: t('pages.audit_logs.columns.record_type'),
        sortable: false,
        width: 200,
      },
      {
        field: 'recordId',
        headerName: t('common.labels.id'),
        sortable: false,
        width: 65,
      },
      {
        field: 'recordName',
        headerName: t('common.labels.name'),
        sortable: false,
        flex: 1,
        minWidth: 200,
      },
    ],
    [t],
  );

  if (error) return <Navigate replace to="/not-found" />;

  return (
    <LoadingPane in={loading && !data} size={80} thickness={4}>
      <>
        <ReadOnlyDataGrid
          columns={columns}
          loading={loading}
          rows={records}
          hideFooter
          pagination={false}
          slots={{
            columnResizeIcon: () => null,
            noRowsOverlay: NoRowsOverlay,
          }}
        />
        <ListPagination totalCount={totalCount} />
      </>
    </LoadingPane>
  );
};
